import {gql} from '@apollo/client';

/**
 * Instrument association helpers.
 *
 * Website news, and anything else that stores a list of FinancialInstrument
 * ids, has the same three obligations: submit ids and nothing else, submit
 * each id once, and be able to render a saved id as a label on an edit form.
 */

/** Every id is a 24-character MongoDB ObjectId; the backend rejects anything else. */
export const isInstrumentId = value =>
  typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value);

/**
 * The array to submit: valid ids, each once, in the order they were picked.
 *
 * Legacy records return `instrumentIds: null`, so form state normalises
 * through here too and an untouched legacy article reads as `[]`.
 */
export const normalizeInstrumentIds = ids => [
  ...new Set((ids ?? []).filter(isInstrumentId)),
];

/* The union has six members and each needs its own inline fragment, so the
   selection is written once and reused by both the list query and the
   by-ids resolver below. */
const OPTION_FIELDS = `
  ... on PublicEquity { id name ticker icon key status }
  ... on Bond { id name ticker icon key status }
  ... on MutualFund { id name ticker icon key status }
  ... on Ipo { id name ticker icon key status }
  ... on PrivateEquity { id name ticker icon key status }
  ... on VentureFund { id name ticker icon key status offeringName }
`;

/**
 * Resolves several saved ids in one request.
 *
 * `getFinancialInstrument` takes a single id, so an edit form holding five
 * associations would otherwise need five queries — and a hook per id is not
 * something a component can do. Aliasing them into one document is the same
 * approach the exchange totals query takes.
 *
 * Ids are validated as hex before they reach the template, so nothing but an
 * ObjectId is ever interpolated.
 */
export const buildInstrumentOptionsByIdsQuery = ids => {
  const wanted = normalizeInstrumentIds(ids);

  // A component builds this document while rendering, before it can decide to
  // skip the query — so with nothing to resolve it still has to be a document
  // that parses. An empty selection set is a syntax error, not an empty query.
  if (!wanted.length) {
    return gql`
      query AdminInstrumentOptionsByIds {
        __typename
      }
    `;
  }

  return gql`
    query AdminInstrumentOptionsByIds {
      ${wanted
        .map(
          (id, index) => `
        option${index}: getFinancialInstrument(id: "${id}") {
          ... on ResponseWithFinancialInstrument {
            data { ${OPTION_FIELDS} }
          }
        }`,
        )
        .join('\n')}
    }
  `;
};

/** Pulls the instruments out of the aliased response above. */
export const readInstrumentOptionsByIds = data =>
  Object.values(data ?? {})
    .map(entry => entry?.data)
    .filter(option => option?.id);

/** True when the built document actually asks for something. */
export const hasInstrumentIdsToResolve = ids =>
  normalizeInstrumentIds(ids).length > 0;
